/**
 * Icon components and utilities for replacing emoji with Lucide icons
 * This provides a centralized place for all icon mappings and reusable icon components
 */

import {
  // Status indicators
  CheckCircle,
  XCircle,
  AlertTriangle,
  Construction,

  // Navigation/Location
  MapPin,
  Map,
  Home,
  Navigation,

  // Difficulty circles - using Circle with different colors
  Circle,

  // Ratings
  Star,
  Trophy,
  Gem,
  Medal,

  // Trail styles
  Mountain,
  Waves,
  Wrench,
  Plane,
  Bike,
  TreePine,

  // Measurements/Stats
  Ruler,
  ArrowDown,
  ArrowUp,
  Timer,
  Gauge,
  TrendingUp,
  BarChart3,

  // Facilities/Amenities
  Hotel,
  House,
  Tent,
  Bed,
  Car,
  ParkingCircle,
  Lock,
  Droplets,
  Utensils,
  Wifi,
  GraduationCap,
  Phone,
  CableCar,
  Users,
  DollarSign,
  Lightbulb,
  Moon,
  Trees,
  Building2,
  Target,

  // Actions/UI
  ThumbsUp,
  ThumbsDown,
  Share2,
  List,
  Tag,
  Calendar,
  Flower2,
  Sun,
  Leaf,
  Snowflake,
  Truck,
  RotateCw,
  Shield,
  Zap,
  MessageCircle,
  BookOpen,
  X,
  Menu,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Flag,
  Rocket,
  Sparkles,
  Flame,
  Dumbbell,
  Skull,
  Hand,
  Wind,

  type LucideIcon,
  type LucideProps,
} from 'lucide-react';
import React from 'react';

// Re-export all icons for easy importing
export {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Construction,
  MapPin,
  Map,
  Home,
  Navigation,
  Circle,
  Star,
  Trophy,
  Gem,
  Medal,
  Mountain,
  Waves,
  Wrench,
  Plane,
  Bike,
  TreePine,
  Ruler,
  ArrowDown,
  ArrowUp,
  Timer,
  Gauge,
  TrendingUp,
  BarChart3,
  Hotel,
  House,
  Tent,
  Bed,
  Car,
  ParkingCircle,
  Lock,
  Droplets,
  Utensils,
  Wifi,
  GraduationCap,
  Phone,
  CableCar,
  Users,
  DollarSign,
  Lightbulb,
  Moon,
  Trees,
  Building2,
  Target,
  ThumbsUp,
  ThumbsDown,
  Share2,
  List,
  Tag,
  Calendar,
  Flower2,
  Sun,
  Leaf,
  Snowflake,
  Truck,
  RotateCw,
  Shield,
  Zap,
  MessageCircle,
  BookOpen,
  X,
  Menu,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Flag,
  Rocket,
  Sparkles,
  Flame,
  Dumbbell,
  Skull,
  Hand,
  Wind,
};

// Type exports
export type { LucideIcon, LucideProps };

// Default icon props
const defaultIconProps: LucideProps = {
  size: 16,
  strokeWidth: 2,
};

// Difficulty level colors
export const difficultyColors = {
  green: '#22c55e',
  blue: '#3b82f6',
  red: '#ef4444',
  black: '#1f2937',
  'double-black': '#0a0a0a',
} as const;

// Status icon component
interface StatusIconProps {
  status: 'open' | 'closed' | 'maintenance';
  size?: number;
  className?: string;
}

export const StatusIcon: React.FC<StatusIconProps> = ({ status, size = 16, className = '' }) => {
  const iconProps = { size, className, strokeWidth: 2 };

  switch (status) {
    case 'open':
      return <CheckCircle {...iconProps} className={`text-green-500 ${className}`} />;
    case 'closed':
      return <XCircle {...iconProps} className={`text-red-500 ${className}`} />;
    case 'maintenance':
      return <Construction {...iconProps} className={`text-yellow-500 ${className}`} />;
    default:
      return null;
  }
};

// Difficulty badge component with colored circle
interface DifficultyBadgeProps {
  difficulty: 'green' | 'blue' | 'red' | 'black' | 'double-black';
  size?: number;
  showLabel?: boolean;
  className?: string;
}

export const DifficultyBadge: React.FC<DifficultyBadgeProps> = ({
  difficulty,
  size = 14,
  showLabel = true,
  className = ''
}) => {
  const color = difficultyColors[difficulty] || '#gray';
  const labels: Record<string, string> = {
    green: 'Zielona',
    blue: 'Niebieska',
    red: 'Czerwona',
    black: 'Czarna',
    'double-black': 'Podwójnie czarna',
  };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <Circle size={size} fill={color} color={color} strokeWidth={0} />
      {difficulty === 'double-black' && (
        <Circle size={size} fill={color} color={color} strokeWidth={0} />
      )}
      {showLabel && <span>{labels[difficulty]}</span>}
    </span>
  );
};

// Trail style icon mapping
const trailStyleIcons: Record<string, { icon: LucideIcon; label: string }> = {
  downhill: { icon: Mountain, label: 'Downhill' },
  flow: { icon: Waves, label: 'Flow' },
  technical: { icon: Wrench, label: 'Technical' },
  jump: { icon: Plane, label: 'Jump' },
  enduro: { icon: Bike, label: 'Enduro' },
  freeride: { icon: Hand, label: 'Freeride' },
  'all-mountain': { icon: Mountain, label: 'All-Mountain' },
  'north-shore': { icon: TreePine, label: 'North Shore' },
};

interface TrailStyleBadgeProps {
  style: string;
  size?: number;
  showLabel?: boolean;
  className?: string;
}

export const TrailStyleBadge: React.FC<TrailStyleBadgeProps> = ({
  style,
  size = 16,
  showLabel = true,
  className = ''
}) => {
  const styleConfig = trailStyleIcons[style];
  if (!styleConfig) return null;

  const IconComponent = styleConfig.icon;

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <IconComponent size={size} strokeWidth={2} />
      {showLabel && <span>{styleConfig.label}</span>}
    </span>
  );
};

// Stat icon component for trail statistics
type StatType = 'length' | 'drop' | 'climb' | 'gradient' | 'time' | 'flow' | 'technical';

const statIcons: Record<StatType, LucideIcon> = {
  length: Ruler,
  drop: ArrowDown,
  climb: ArrowUp,
  gradient: Gauge,
  time: Timer,
  flow: Waves,
  technical: Wrench,
};

interface StatIconProps {
  type: StatType;
  size?: number;
  className?: string;
}

export const StatIcon: React.FC<StatIconProps> = ({ type, size = 16, className = '' }) => {
  const IconComponent = statIcons[type];
  if (!IconComponent) return null;

  return <IconComponent size={size} strokeWidth={2} className={className} />;
};

// Star rating component
interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: number;
  className?: string;
}

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 16,
  className = ''
}) => {
  return (
    <span className={`inline-flex items-center ${className}`}>
      {[...Array(maxRating)].map((_, i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={2}
          fill={i < rating ? 'currentColor' : 'none'}
          className={i < rating ? 'text-yellow-400' : 'text-gray-300'}
        />
      ))}
    </span>
  );
};

// Accommodation type icons
const accommodationTypeIcons: Record<string, LucideIcon> = {
  hotel: Hotel,
  pension: House,
  apartment: Building2,
  camping: Tent,
  hostel: Bed,
  house: House,
  campsite: Trees,
  campervan: Car,
};

interface AccommodationTypeIconProps {
  type: string;
  size?: number;
  className?: string;
}

export const AccommodationTypeIcon: React.FC<AccommodationTypeIconProps> = ({
  type,
  size = 16,
  className = ''
}) => {
  const IconComponent = accommodationTypeIcons[type.toLowerCase()] || Hotel;
  return <IconComponent size={size} strokeWidth={2} className={className} />;
};

// Facility icon component
type FacilityType = 'bikeStorage' | 'bikeWash' | 'bikeService' | 'parking' | 'restaurant' |
                   'wifi' | 'school' | 'lift' | 'familyFriendly' | 'lighting' | 'fencing';

const facilityIcons: Record<FacilityType, LucideIcon> = {
  bikeStorage: Lock,
  bikeWash: Droplets,
  bikeService: Wrench,
  parking: ParkingCircle,
  restaurant: Utensils,
  wifi: Wifi,
  school: GraduationCap,
  lift: CableCar,
  familyFriendly: Users,
  lighting: Lightbulb,
  fencing: Trees,
};

interface FacilityIconProps {
  type: FacilityType;
  size?: number;
  className?: string;
}

export const FacilityIcon: React.FC<FacilityIconProps> = ({ type, size = 16, className = '' }) => {
  const IconComponent = facilityIcons[type];
  if (!IconComponent) return null;

  return <IconComponent size={size} strokeWidth={2} className={className} />;
};

// Season icons
type Season = 'spring' | 'summer' | 'autumn' | 'winter';

const seasonIcons: Record<Season, LucideIcon> = {
  spring: Flower2,
  summer: Sun,
  autumn: Leaf,
  winter: Snowflake,
};

interface SeasonIconProps {
  season: Season;
  size?: number;
  className?: string;
}

export const SeasonIcon: React.FC<SeasonIconProps> = ({ season, size = 16, className = '' }) => {
  const IconComponent = seasonIcons[season];
  if (!IconComponent) return null;

  return <IconComponent size={size} strokeWidth={2} className={className} />;
};

// Icon with label component for consistent styling
interface IconWithLabelProps {
  icon: LucideIcon;
  label: string;
  size?: number;
  className?: string;
  iconClassName?: string;
}

export const IconWithLabel: React.FC<IconWithLabelProps> = ({
  icon: IconComponent,
  label,
  size = 16,
  className = '',
  iconClassName = ''
}) => {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <IconComponent size={size} strokeWidth={2} className={iconClassName} />
      <span>{label}</span>
    </span>
  );
};
